import { createFileRoute, redirect } from "@tanstack/react-router";
import {
  cmsRouteConfig,
  withSiteGlobals,
} from "@decocms/tanstack";
import { DecoPageRenderer } from "@decocms/tanstack";
import { deferredSectionLoader } from "@decocms/tanstack/sdk/deferredSectionLoader";
import { headFromSeoSections } from "~/components/seo/seoHead";
import { SITE } from "~/constants";
import { isCacheStrippedParam } from "~/sdk/cacheKeyParams";

// `withSiteGlobals` wraps the loader so that sections declared in
// `.deco/blocks/Site.json` under `global` (Minicart drawer, Fonts,
// LowStockModal, etc.) are merged into `data.resolvedSections` on every
// page. Without it the post-add-to-cart Minicart notification drawer had
// no subscriber and never appeared on PLPs / PDPs.
const routeConfig = withSiteGlobals(cmsRouteConfig({
  siteName: SITE.NAME,
  defaultTitle: SITE.DEFAULT_TITLE,
  defaultDescription:
    "Comprar Produtos Eróticos com Preço de Atacado é na Miess, o maior Sex Shop do Brasil com pgto 10X sem Juros e Frete Grátis SP acima de R$ 250 em compras",
  // `skuId` must reach loaderDeps: the VTEX page loader honors ?skuId=
  // (returns that SKU's images/offers) and variant clicks on the PDP are SPA
  // navigations where only skuId changes (DECO-5445). With skuId ignored,
  // client navigations never re-ran the loader and the gallery never swapped.
  ignoreSearchParams: [],
  // Suppress the framework's pending UI for fast filter/sort transitions on
  // PLPs; the eager SearchResult section stays mounted and only the product
  // grid skeletons via TanStack Router's isLoading state. Mirrors miami.
  pendingMs: 60_000,
  pendingMinMs: 0,
}));

// Section loaders can't redirect directly: `runSectionLoaders` swallows
// anything they throw. Sections that decide a redirect server-side (e.g.
// Scripts/RedirectDepartments on empty categories hit through legacy ?map=
// URLs, DECO-5821) return a `__redirect` marker in their props instead.
function sectionRedirect(
  page: { resolvedSections?: Array<{ props?: Record<string, unknown> }> } | null,
): { href: string; status: number } | null {
  for (const section of page?.resolvedSections ?? []) {
    const marker = section?.props?.__redirect as
      | { href?: string; status?: number }
      | undefined;
    if (marker?.href) return { href: marker.href, status: marker.status ?? 301 };
  }
  return null;
}

export const Route = createFileRoute("/$")({
  ...routeConfig,
  // O match ignora o que a chave de cache do edge ignora (~/sdk/cacheKeyParams).
  // Sem isso, `/x?utm_source=…` gravava na chave `/x` o HTML de outro match e
  // quem chegava em `/x` hidratava sem ele: React #418 e a árvore refeita.
  loaderDeps: (opts: Parameters<NonNullable<typeof routeConfig.loaderDeps>>[0]) =>
    routeConfig.loaderDeps!({
      ...opts,
      search: Object.fromEntries(
        Object.entries(opts.search ?? {}).filter(([k]) => !isCacheStrippedParam(k)),
      ),
    }),
  loader: async (ctx: Parameters<NonNullable<typeof routeConfig.loader>>[0]) => {
    const page = await routeConfig.loader!(ctx);
    const target = sectionRedirect(page);
    if (target) {
      throw redirect({
        href: target.href,
        statusCode: target.status,
        replace: true,
      });
    }
    return page;
  },
  // The site's SEO sections compute the full head payload in their loaders;
  // emit it here as the single source of truth. Letting the framework head
  // AND the section render tags duplicated <title>/<meta description>/<meta
  // robots> on every page (DECO-5439/5440/5444).
  head: (input: Parameters<NonNullable<typeof routeConfig.head>>[0]) =>
    headFromSeoSections(
      input.loaderData as Parameters<typeof headFromSeoSections>[0],
      routeConfig.head!(input),
    ),
  component: CmsPage,
  notFoundComponent: NotFoundPage,
});

function CmsPage() {
  const data = Route.useLoaderData() as Record<string, any> | null;
  if (!data) return <NotFoundPage />;

  return (
    <DecoPageRenderer
      sections={data.resolvedSections ?? []}
      deferredSections={data.deferredSections ?? []}
      deferredPromises={data.deferredPromises}
      pagePath={data.pagePath}
      pageUrl={data.pageUrl}
      loadDeferredSectionFn={deferredSectionLoader}
    />
  );
}

function NotFoundPage() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-6xl font-bold text-base-content/20 mb-4">404</h1>
        <h2 className="text-2xl font-bold mb-2">Page Not Found</h2>
        <p className="text-base-content/60 mb-6">No CMS page block matches this URL.</p>
        <a href="/" className="btn btn-primary">Go Home</a>
      </div>
    </div>
  );
}
